import React, { useState, useEffect } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { Plus, Edit2, Trash2, Download, Briefcase, CreditCard, CheckCircle2, AlertCircle } from 'lucide-react';
import Modal from '../components/Modal';
import { setupExpensesService } from '../services/supabaseService';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend
} from 'recharts';

const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  
  const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0].split(' ')[0] : '';
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

const formatDateLocal = (isoStr) => {
  if (!isoStr) return '';
  const d = parseDateSafe(isoStr);
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getMonthShortName = (isoStr) => {
  if (!isoStr) return '';
  const d = parseDateSafe(isoStr);
  return d ? d.toLocaleString('default', { month: 'short' }) : '';
};

const InitialSetupExpenses = () => {
  const [setupExpenses, setSetupExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useSessionStorage('setup_isModalOpen', false);
  const [editingExpense, setEditingExpense] = useSessionStorage('setup_editingExpense', null);
  const [searchQuery, setSearchQuery] = useSessionStorage('setup_searchQuery', '');
  const [filterCategory, setFilterCategory] = useSessionStorage('setup_filterCategory', 'All');
  const [filterStatus, setFilterStatus] = useSessionStorage('setup_filterStatus', 'All');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Form State
  const [formData, setFormData] = useSessionStorage('setup_formData', {
    itemName: '', amount: '', category: 'Security Deposit/Lease', paymentMode: 'UPI', paymentDate: '', status: 'Paid', notes: ''
  });

  const categories = [
    'Security Deposit/Lease',
    'Interior Work',
    'Painting',
    'Plumbing',
    'Electricals',
    'Furniture & Appliances',
    'Home Furnishing Expenses',
    'Carpenter Hardware Items Expenses',
    'Labour Payment',
    'Office Supplies',
    'Transportation',
    'Printing & Marketing Expenses',
    'Legal/Licensing',
    'Marketing/Launch',
    'Other'
  ];

  const paymentModes = [
    'Bank Transfer',
    'UPI',
    'Cash',
    'Cheque',
    'Credit Card'
  ];

  const fetchSetupExpenses = async () => {
    try {
      setLoading(true);
      const data = await setupExpensesService.getAll();
      const normalized = (Array.isArray(data) ? data : []).map(row => ({
        ...row,
        itemName: row.itemName || row.item_name || '',
        paymentMode: row.paymentMode || row.payment_mode || 'UPI',
        paymentDate: row.paymentDate || (row.payment_date ? row.payment_date.split('T')[0] : '')
      }));
      setSetupExpenses(normalized);
    } catch (err) {
      console.error('Error fetching setup expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetupExpenses();
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleOpenModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        itemName: expense.itemName || '',
        amount: expense.amount || '',
        category: expense.category || 'Security Deposit/Lease',
        paymentMode: expense.paymentMode || 'UPI',
        paymentDate: formatDateLocal(expense.paymentDate),
        status: expense.status || 'Paid',
        notes: expense.notes || ''
      });
    } else {
      setEditingExpense(null);
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      setFormData({
        itemName: '',
        amount: '',
        category: 'Security Deposit/Lease',
        paymentMode: 'UPI',
        paymentDate: today,
        status: 'Paid',
        notes: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      item_name: formData.itemName,
      amount: Number(formData.amount) || 0,
      category: formData.category,
      payment_mode: formData.paymentMode || 'UPI',
      payment_date: formData.paymentDate,
      status: formData.status || 'Paid',
      notes: formData.notes || ''
    };

    try {
      if (editingExpense) {
        await setupExpensesService.update(editingExpense.id, payload);
      } else {
        await setupExpensesService.create(payload);
      }
      setIsModalOpen(false);
      fetchSetupExpenses();
      // Trigger storage update for investment calculation integration
      window.dispatchEvent(new Event('setup-expenses-updated'));
    } catch (err) {
      console.error('Error saving setup expense:', err);
      alert('Failed to save setup expense: ' + err.message);
    }
  };

  const handleDelete = (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await setupExpensesService.delete(deleteConfirmId);
      setDeleteConfirmId(null);
      fetchSetupExpenses();
      window.dispatchEvent(new Event('setup-expenses-updated'));
    } catch (err) {
      console.error('Error deleting setup expense:', err);
      alert('Failed to delete item: ' + err.message);
    }
  };

  // Calculations
  const totalCapEx = setupExpenses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const totalPaid = setupExpenses.filter(item => item.status === 'Paid').reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const totalPending = setupExpenses.filter(item => item.status === 'Pending').reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const totalItemsCount = setupExpenses.length;

  // Filter Logic
  const filteredExpenses = setupExpenses.filter(item => {
    const matchesSearch = item.itemName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (item.notes || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'All' ? true : item.category === filterCategory;
    const matchesStatus = filterStatus === 'All' ? true : item.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Chart Data preparation
  const categoryChartData = Object.entries(
    setupExpenses.reduce((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + Number(item.amount || 0);
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  const paymentModeChartData = Object.entries(
    setupExpenses.reduce((acc, item) => {
      acc[item.paymentMode] = (acc[item.paymentMode] || 0) + Number(item.amount || 0);
      return acc;
    }, {})
  ).map(([name, amount]) => ({ name, amount }));

  const CHART_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#84cc16', '#a855f7', '#f97316'];

  const handleExport = () => {
    const headers = ['Setup Item', 'Category', 'Amount (₹)', 'Payment Date', 'Payment Mode', 'Status', 'Notes'];
    const rows = [headers];
    filteredExpenses.forEach(item => {
      rows.push([
        `"${item.itemName.replace(/"/g, '""')}"`,
        `"${item.category}"`,
        item.amount,
        formatDateLocal(item.paymentDate),
        item.paymentMode,
        item.status,
        `"${(item.notes || '').replace(/"/g, '""')}"`
      ]);
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Initial_Setup_Expenses_Report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [currentPage, setCurrentPage] = useSessionStorage('setup_currentPage', 1);
  const itemsPerPage = 8;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterCategory, filterStatus]);

  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage) || 1;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage, setCurrentPage]);

  const activePage = Math.min(currentPage, totalPages) || 1;
  const paginatedExpenses = filteredExpenses.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage);

  if (loading && setupExpenses.length === 0) {
    return <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Loading setup expenses data...</div>;
  }

  return (
    <div>
      {/* Header section */}
      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <div>
          <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Initial Setup Expenses</h2>
          <p style={{ margin: '0.25rem 0 0 0' }}>Manage one-time launch costs, lease deposits, and capital assets (CapEx).</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button className="btn-primary flex items-center gap-2" onClick={handleExport} style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-primary)' }}>
            <Download size={16} /> Export CSV
          </button>
          <button className="btn-primary flex items-center gap-2" onClick={() => handleOpenModal()}>
            <Plus size={16} /> Add Setup Expense
          </button>
        </div>
      </div>

      {/* KPI Stats Row */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {/* Total Setup CapEx */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem', borderTop: '3px solid #8b5cf6' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Briefcase size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Total CapEx Budget</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              ₹{totalCapEx.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Paid Amount */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem', borderTop: '3px solid #10b981' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle2 size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Actual Outflow (Paid)</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--accent-color)' }}>
              ₹{totalPaid.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Pending Amount */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem', borderTop: '3px solid #f59e0b' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertCircle size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Outstanding (Pending)</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#f59e0b' }}>
              ₹{totalPending.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Items Tracked */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem', borderTop: '3px solid #3b82f6' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CreditCard size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Items Configured</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {totalItemsCount} Expense Entries
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Charts */}
      {isMounted && setupExpenses.length > 0 && (
        <div className="grid grid-cols-2 gap-6 mb-8">
          {/* Doughnut Chart of Category Distribution */}
          <div className="card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ fontSize: '1rem', marginBottom: '1.25rem', marginTop: 0, fontWeight: '600' }}>Setup Budget Allocation by Category</h3>
            <div style={{ width: '100%', height: '240px', position: 'relative' }}>
              <ResponsiveContainer width="99%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {categoryChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Cost']}
                  />
                  <Legend 
                    layout="horizontal" 
                    verticalAlign="bottom" 
                    align="center"
                    wrapperStyle={{ fontSize: '0.75rem', paddingTop: '10px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bar Chart of Payment Modes */}
          <div className="card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ fontSize: '1rem', marginBottom: '1.25rem', marginTop: 0, fontWeight: '600' }}>Outflow Channels (Payment Modes)</h3>
            <div style={{ width: '100%', height: '240px', position: 'relative' }}>
              <ResponsiveContainer width="99%" height="100%">
                <BarChart data={paymentModeChartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fontSize: 11 }} />
                  <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 11 }} formatter={v => `₹${v >= 100000 ? `${v / 1000}k` : v}`} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Total Spent']}
                  />
                  <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={45}>
                    {paymentModeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[(index + 1) % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Filter and Table Section */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Recorded Budget Items ({filteredExpenses.length})</h3>
          
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', flex: '1', justifyContent: 'flex-end' }}>
            {/* Search Bar */}
            <input 
              type="text" 
              placeholder="Search items or notes..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ maxWidth: '250px', padding: '0.5rem 1rem', borderRadius: '0.5rem' }}
            />

            {/* Category Filter */}
            <select 
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
            >
              <option value="All" style={{ color: '#000' }}>All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat} style={{ color: '#000' }}>{cat}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select 
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
            >
              <option value="All" style={{ color: '#000' }}>All Statuses</option>
              <option value="Paid" style={{ color: '#000' }}>Paid</option>
              <option value="Pending" style={{ color: '#000' }}>Pending</option>
            </select>
          </div>
        </div>

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '1rem 0.5rem', fontWeight: '500', minWidth: '120px' }}>Payment Date</th>
                <th style={{ padding: '1rem 0.5rem', fontWeight: '500', minWidth: '180px' }}>Setup Item</th>
                <th style={{ padding: '1rem 0.5rem', fontWeight: '500' }}>Category</th>
                <th style={{ padding: '1rem 0.5rem', fontWeight: '500' }}>Amount</th>
                <th style={{ padding: '1rem 0.5rem', fontWeight: '500' }}>Payment Mode</th>
                <th style={{ padding: '1rem 0.5rem', fontWeight: '500' }}>Status</th>
                <th style={{ padding: '1rem 0.5rem', fontWeight: '500', minWidth: '150px' }}>Notes</th>
                <th style={{ padding: '1rem 0.5rem', fontWeight: '500', textAlign: 'right', minWidth: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '3rem 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    No setup expense records match the selected filters.
                  </td>
                </tr>
              ) : (
                paginatedExpenses.map((expense) => (
                  <tr key={expense.id} style={{ borderBottom: '1px solid var(--border-color)', transition: 'background-color 0.2s' }}>
                    {/* Date */}
                    <td style={{ padding: '1rem 0.5rem', whiteSpace: 'nowrap' }}>
                      {expense.paymentDate ? (() => {
                        const d = parseDateSafe(expense.paymentDate);
                        return d ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ 
                              width: '38px', height: '38px', 
                              borderRadius: '6px', 
                              backgroundColor: 'rgba(139, 92, 246, 0.1)',
                              color: '#8b5cf6',
                              display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
                            }}>
                              <span style={{ fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', lineHeight: '1' }}>
                                {getMonthShortName(expense.paymentDate)}
                              </span>
                              <span style={{ fontSize: '0.95rem', fontWeight: '700', lineHeight: '1', marginTop: '2px' }}>
                                {d.getDate()}
                              </span>
                            </div>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                              {d.getFullYear()}
                            </span>
                          </div>
                        ) : '-';
                      })() : '-'}
                    </td>
                    
                    {/* Item Name */}
                    <td style={{ padding: '1rem 0.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                      {expense.itemName}
                    </td>

                    {/* Category */}
                    <td style={{ padding: '1rem 0.5rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.65rem', 
                        borderRadius: '9999px', 
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        fontSize: '0.75rem',
                        color: 'var(--text-primary)',
                        border: '1px solid rgba(255,255,255,0.05)'
                      }}>
                        {expense.category}
                      </span>
                    </td>

                    {/* Amount */}
                    <td style={{ padding: '1rem 0.5rem', fontWeight: '700', color: expense.status === 'Paid' ? 'var(--text-primary)' : '#f59e0b' }}>
                      ₹{Number(expense.amount).toLocaleString()}
                    </td>

                    {/* Payment Mode */}
                    <td style={{ padding: '1rem 0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      {expense.paymentMode}
                    </td>

                    {/* Status */}
                    <td style={{ padding: '1rem 0.5rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.65rem', 
                        borderRadius: '9999px', 
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        backgroundColor: expense.status === 'Paid' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                        color: expense.status === 'Paid' ? 'var(--accent-color)' : '#f59e0b'
                      }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: expense.status === 'Paid' ? 'var(--accent-color)' : '#f59e0b' }}></span>
                        {expense.status}
                      </span>
                    </td>

                    {/* Notes */}
                    <td style={{ padding: '1rem 0.5rem', color: 'var(--text-secondary)', fontSize: '0.8rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={expense.notes}>
                      {expense.notes || '-'}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '1rem 0.5rem', textAlign: 'right' }}>
                      <div className="flex justify-end gap-1">
                        <button onClick={() => handleOpenModal(expense)} style={{ padding: '0.4rem', background: 'transparent', color: 'var(--text-secondary)' }}>
                          <Edit2 size={15} />
                        </button>
                        <button onClick={() => handleDelete(expense.id)} style={{ padding: '0.4rem', background: 'transparent', color: 'var(--danger-color)' }}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 0.5rem', borderTop: '1px solid var(--border-color)', marginTop: '1rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Showing {(activePage - 1) * itemsPerPage + 1} to {Math.min(activePage * itemsPerPage, filteredExpenses.length)} of {filteredExpenses.length} entries
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={activePage === 1}
                style={{ padding: '0.4rem 0.85rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', background: 'transparent', cursor: activePage === 1 ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', opacity: activePage === 1 ? 0.5 : 1, fontSize: '0.8rem' }}
              >
                Previous
              </button>
              <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                Page {activePage} of {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={activePage === totalPages}
                style={{ padding: '0.4rem 0.85rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', background: 'transparent', cursor: activePage === totalPages ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', opacity: activePage === totalPages ? 0.5 : 1, fontSize: '0.8rem' }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Record/Edit Expense Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingExpense ? "Edit Setup Expense" : "Record Setup Expense"}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Setup Item / Description</label>
            <input 
              required 
              type="text" 
              value={formData.itemName} 
              onChange={e => setFormData({...formData, itemName: e.target.value})} 
              placeholder="e.g. Interior Woodwork Deposit" 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Amount (₹)</label>
              <input 
                required 
                type="number" 
                value={formData.amount} 
                onChange={e => setFormData({...formData, amount: e.target.value})} 
                placeholder="e.g. 50000" 
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Payment Date</label>
              <input 
                required 
                type="date" 
                value={formData.paymentDate} 
                onChange={e => setFormData({...formData, paymentDate: e.target.value})} 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Category</label>
              <select 
                value={formData.category} 
                onChange={e => setFormData({...formData, category: e.target.value})}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat} style={{ color: '#000' }}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Payment Mode</label>
              <select 
                value={formData.paymentMode} 
                onChange={e => setFormData({...formData, paymentMode: e.target.value})}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                {paymentModes.map(mode => (
                  <option key={mode} value={mode} style={{ color: '#000' }}>{mode}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Status</label>
              <select 
                value={formData.status} 
                onChange={e => setFormData({...formData, status: e.target.value})}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="Paid" style={{ color: '#000' }}>Paid</option>
                <option value="Pending" style={{ color: '#000' }}>Pending</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Notes / Remarks (Optional)</label>
            <input 
              type="text" 
              value={formData.notes} 
              onChange={e => setFormData({...formData, notes: e.target.value})} 
              placeholder="e.g. Paid via online banking, receipt attached" 
            />
          </div>

          <button type="submit" className="btn-primary mt-4">
            {editingExpense ? 'Update Setup Expense' : 'Save Setup Expense'}
          </button>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} title="Delete Setup Expense">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
            Are you sure you want to delete this initial setup expense? This action is permanent and will remove it from all Capital Expenditure calculations.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button 
              type="button"
              onClick={() => setDeleteConfirmId(null)} 
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: '0.5rem', 
                border: '1px solid var(--border-color)', 
                background: 'rgba(255,255,255,0.05)', 
                color: 'var(--text-primary)', 
                cursor: 'pointer', 
                fontSize: '0.875rem', 
                fontWeight: '500' 
              }}
            >
              Cancel
            </button>
            <button 
              type="button"
              onClick={confirmDelete} 
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: '0.5rem', 
                background: 'var(--danger-color)', 
                color: '#fff', 
                cursor: 'pointer', 
                fontSize: '0.875rem', 
                fontWeight: '500' 
              }}
            >
              Delete Record
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InitialSetupExpenses;
